import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader } from 'lucide-react';

const ActionApproval = ({ proposedActions, onApprove, onCancel, isExecuting = false }) => {
  const [selectedActions, setSelectedActions] = useState(
    proposedActions.map(action => ({
      ...action,
      approved: action.validated // Auto-approve validated actions
    }))
  );

  const handleToggleAction = (actionId) => {
    setSelectedActions(prev =>
      prev.map(action =>
        action.id === actionId
          ? { ...action, approved: !action.approved }
          : action
      )
    );
  };

  const handleApprove = () => {
    // Only approve validated actions
    const approved = selectedActions.filter(a => a.approved && a.validated);
    if (approved.length === 0) {
      alert('Please select at least one validated action to approve.');
      return;
    }
    onApprove(approved);
  };

  const approvedCount = selectedActions.filter(a => a.approved && a.validated).length;
  const hasInvalidActions = selectedActions.some(a => !a.validated);
  const hasValidActions = selectedActions.some(a => a.validated);

  return (
    <div className="action-approval bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-3">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Action Approval Required
          </h3>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          disabled={isExecuting}
        >
          <XCircle className="h-5 w-5" />
        </button>
      </div>

      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
        Please review and approve the following actions before they are executed:
      </p>

      <div className="space-y-2 mb-4">
        {selectedActions.map((action) => (
          <div
            key={action.id}
            className={`p-3 rounded border ${
              action.validated
                ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
            }`}
          >
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                checked={action.approved}
                onChange={() => handleToggleAction(action.id)}
                disabled={isExecuting || !action.validated}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {action.description || action.name}
                  </span>
                  {action.validated ? (
                    <CheckCircle className="h-4 w-4 text-green-500" title="Validated" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-yellow-500" title="Validation errors" />
                  )}
                </div>
                {action.reasoning && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    {action.reasoning}
                  </p>
                )}
                {!action.validated && action.validationErrors && (
                  <div className="text-xs text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 p-2 rounded mt-2">
                    <strong className="flex items-center space-x-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>Validation Issues:</span>
                    </strong>
                    <ul className="list-disc list-inside mt-1 space-y-0.5">
                      {Object.entries(action.validationErrors)
                        .filter(([key]) => !key.startsWith('_')) // Exclude metadata
                        .map(([key, error]) => (
                          <li key={key}>{error}</li>
                        ))}
                    </ul>
                    {action.missingFields && action.missingFields.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-yellow-300 dark:border-yellow-700">
                        <strong>Missing Required Fields:</strong>
                        <ul className="list-disc list-inside mt-1">
                          {action.missingFields.map((field, idx) => (
                            <li key={idx}>
                              {field.displayName || field.name}
                              {field.condition && ` (when ${JSON.stringify(field.condition)})`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {action.requiredFields && action.requiredFields.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-yellow-300 dark:border-yellow-700">
                        <strong>Required Fields for this entity:</strong>
                        <ul className="list-disc list-inside mt-1">
                          {action.requiredFields
                            .filter(f => f.required)
                            .map((field, idx) => (
                              <li key={idx}>{field.displayName || field.name}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {action.parameters && Object.keys(action.parameters).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                      View Parameters
                    </summary>
                    <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-x-auto">
                      {JSON.stringify(action.parameters, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasInvalidActions && (
        <div className="mb-3 p-2 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded text-xs text-yellow-800 dark:text-yellow-200">
          Some actions have validation errors and cannot be approved.
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {approvedCount} of {selectedActions.length} action(s) selected
        </span>
        <div className="flex space-x-2">
          <button
            onClick={onCancel}
            disabled={isExecuting}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApprove}
            disabled={isExecuting || approvedCount === 0 || !hasValidActions}
            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
            title={!hasValidActions ? 'No valid actions to approve' : approvedCount === 0 ? 'Please select at least one action' : ''}
          >
            {isExecuting ? (
              <>
                <Loader className="h-3 w-3 animate-spin" />
                <span>Executing...</span>
              </>
            ) : (
              <span>Approve & Execute ({approvedCount})</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionApproval;

